import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaClipboardList, FaEye } from "react-icons/fa";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const AdmissionSeatAllocationConfigList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  useEffect(() => {
    if (school_id) {
      fetchSeatConfigList();
    }
  }, [school_id, pagination.pageIndex, pagination.pageSize, apiSearch]);

  const fetchSeatConfigList = async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: Number(school_id),
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: apiSearch || "",
      };

      const response = await AxiosInstance.post(
        "/admission/listSeatConfig",
        payload,
      );

      if (response?.data?.status) {
        const configData = response.data.data || [];
        const transformedData = configData.map((item, index) => ({
          id: item.id,
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          academic_year: item.academic_year || "",
          class_name: item.class_name || "",
          medium: item.medium || "",
          group_name: item.name || "",
          total_capacity:
            item.group_max_seat_count || item.section_max_seat_count || "",
        }));
        setData(transformedData);
        setRowCount(response.data.pagination?.total || transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching seat config list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/user/admission-seat-allocation-config/create");
  };

  const handleView = (row) => {
    navigate("/user/admission-seat-allocation-config/view", {
      state: { id: row.id, data: row },
    });
  };

  const columns = useMemo(
    () => [
      {
        id: "sno",
        header: translations?.labels?.serialNo || "S.No",
        size: 80,
        Cell: ({ row }) => (
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            {row.index + 1 + pagination.pageIndex * pagination.pageSize}
          </div>
        ),
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "academic_year",
        header: translations?.labels?.academicYear || "Academic Year",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "class_name",
        header: translations?.labels?.classLabel || "Class",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "medium",
        header: translations?.labels?.medium || "Medium",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: "group_name",
        header: translations?.labels?.groupName || "Group",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value !== null && value !== undefined && value !== ""
            ? value
            : "-";
        },
      },
      {
        accessorKey: "total_capacity",
        header: translations?.labels?.totalCapacity || "Total Capacity",
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value !== null && value !== undefined && value !== ""
            ? value
            : "-";
        },
      },
      {
        id: "actions",
        header: translations?.labels?.actions || "Actions",
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div style={{ textAlign: "center" }}>
            <button
              className={styles.viewBtn}
              onClick={() => handleView(row.original)}
              title={translations?.labels?.view || "View"}
            >
              <FaEye />
            </button>
          </div>
        ),
      },
    ],
    [translations, pagination.pageIndex, pagination.pageSize],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaClipboardList />
        </div>
        <h1 className={styles.headerTitle}>
          {translations?.labels?.seatAllocationConfiguration ||
            "Seat Allocation Configuration"}
        </h1>
      </div>

      {/* Action Button */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          <div className="col-lg-12 px-2 col-md-12 col-sm-12">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleAdd}
              >
                {translations?.labels?.add || "ADD"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        placeholder={translations?.labels?.searchSeatConfig || "Search ..."}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        enableSearch={true}
        enableFiltering={false}
        noRecordsMessage={
          translations?.labels?.noRecordsToDisplay || "No records to display"
        }
      />
    </div>
  );
};

export default AdmissionSeatAllocationConfigList;
